import { FILE_NAME_KEY, FILE_SIZE_KEY } from '../../constants/file.constants';
import { getDsName as getDsNameUtil } from '../../utility/dataSourceHelpers';
import { getNodeData, setNodeData } from '../../utility/nodeDataHelpers';
import { setRichTextString } from '../../utility/richTextHelpers';
import { deleteNode } from '../../utility/xmlHelpers';
import { getNodeValue, ownerDocument, setNodeValue } from '../../xmlUtility';

const $ = typeof window !== 'undefined' && window.jQuery;

const defaultValueKey = 'defVal';

function normalizeValue(v) {
    return v ? v.replace(/\r\n|\r/g, '\n') : '';
}

function setRichTextXml(node, rtNodes) {
    $(node).empty().append($(rtNodes).clone());
}

function dataSourceNode(node, source, xpathEvaluator, listener) {
    var self;

    // -> string
    function value() {
        return getNodeValue(node);
    }

    function evaluateXPath(xpath, options) {
        options = options || {};

        return xpathEvaluator.evaluateXPath(xpath, {
            context: node,
            namespaces: options.namespaces,
        });
    }

    //string -> number
    function evaluateNumber(xpath) {
        return evaluateXPath(xpath).numberValue();
    }

    // string -> boolean
    function evaluateBoolean(xpath) {
        return evaluateXPath(xpath).booleanValue();
    }

    // string -> string
    function evaluateString(xpath) {
        return evaluateXPath(xpath).stringValue();
    }

    function setDefaultValue(val) {
        setNodeData(node, defaultValueKey, val);
    }

    function lastDefaultValue() {
        return getNodeData(node, defaultValueKey);
    }

    function resetFileValueCacheValues(field) {
        if (getNodeData(field, FILE_NAME_KEY)) {
            setNodeData(field, FILE_NAME_KEY, null);
            setNodeData(field, FILE_SIZE_KEY, null);
        }
    }

    const setValueInner = (
        val,
        { setDefault, isRichTextString, isRichTextXml } = {},
    ) => {
        if (isRichTextString) {
            //Move the method to util.js -- 11/15/16
            setRichTextString(node, val);
            return true;
        }
        if (isRichTextXml) {
            setRichTextXml(node, val);
            return true;
        }

        const strVal = String(val);
        const currentValue = value();
        const newValNorm = normalizeValue(strVal);
        const curValNorm = normalizeValue(currentValue);

        // BUGBUG - Is !setDefault correct here?
        if (!setDefault || newValNorm !== normalizeValue(lastDefaultValue())) {
            setNodeValue(node, strVal);

            if (setDefault) {
                setDefaultValue(strVal);
            }

            if (newValNorm !== curValNorm) {
                return true;
            }
        }

        return false;
    };

    // string, [bool = true] -> Promise
    async function setValueAsync(val, options) {
        resetFileValueCacheValues(node);

        const shouldNotify = setValueInner(val, options);

        if (shouldNotify && listener) {
            return await listener.notifyChangeAsync(self);
        }

        return null;
    }

    // DOMNode -> DataSourceNode
    function makeDsNode(n) {
        return dataSourceNode(n, source, xpathEvaluator, listener);
    }

    // String -> [DataSourceNode]
    function selectNodes(xpath, options) {
        return evaluateXPath(xpath, options).toArray().map(makeDsNode);
    }

    // String -> DataSourceNode
    function selectSingle(xpath, options) {
        const selected = evaluateXPath(xpath, options).first();

        return selected ? makeDsNode(selected) : null;
    }

    function getDsName() {
        return getDsNameUtil(node);
    }

    function getNode() {
        return node;
    }

    function childNodes() {
        var cn = node.childNodes || [];

        return Array.prototype.slice.call(cn).map(makeDsNode);
    }

    function localName() {
        return node.localName;
    }

    function namespaceURI() {
        return node.namespaceURI;
    }

    // Appends the specified node to the current node and notifies listeners of a change on the new node.
    // DOMNode -> Promise[DataSourceNode]
    const appendChildAsync = async (newChild) => {
        let newNode = makeDsNode(newChild);

        node.appendChild(newChild);

        source.assignIds(newChild);

        await listener.notifyChangeAsync(newNode);

        return newNode;
    };

    function appendChildrenAsync(newChildren) {
        var childrenNorm = [].concat(newChildren);
        var newNodes = [];

        return childrenNorm
            .reduce(function (prev, next) {
                return prev
                    .then(function () {
                        return appendChildAsync(next);
                    })
                    .then(function (newNode) {
                        newNodes.push(newNode);
                    });
            }, Promise.resolve())
            .then(function () {
                return newNodes;
            });
    }

    //Function to remove current node.
    function deleteSelf() {
        // TODO: re-evaluate default values, etc.
        deleteNode(node);
    }

    /**
     * @param {DOMNode} target the existing sibling of the current node
     *     before which newSibling should be inserted. If falsy, newSibling is inserted as the
     *     last child of the current node's parent
     * @param {DOMNode} newSibling the new sibling to add
     * @returns {Promise<DataSourceNode>} A promise for the new node, which resolves after all ensuing events have complieted
     */
    function insertSiblingAsync(target, newSibling) {
        var newNode = makeDsNode(newSibling),
            parent = node.parentNode || node.ownerDocument;

        parent.insertBefore(newSibling, target);
        source.assignIds(newSibling);

        return listener.notifyChangeAsync(newNode).then(function () {
            return newNode;
        });
    }

    /*
     * Inserts the specified node before the current node
     */
    function insertBeforeAsync(newSibling) {
        return insertSiblingAsync(node, newSibling);
    }

    /*
     * Inserts the specified node after the current node
     */
    function insertAfterAsync(newSibling) {
        // https://developer.mozilla.org/en-US/docs/Web/API/Node.insertBefore
        // We insert after by inserting before the target node's next sibling. If it has no next sibling, it will
        // be added as the last child of the parent
        return insertSiblingAsync(node.nextSibling, newSibling);
    }

    function setContentAsync(nodes) {
        $(node).empty();

        return appendChildrenAsync(nodes);
    }

    function createElement(name) {
        if (!name) {
            return null;
        }

        return ownerDocument(node).createElement(name);
    }

    function createElementNS(namespace, name) {
        if (!(name && namespace)) {
            return null;
        }

        return ownerDocument(node).createElementNS(namespace, name);
    }

    function getAttribute(name) {
        if (!node.getAttribute) {
            return null;
        }

        return node.getAttribute(name);
    }

    self = {
        setValueAsync,
        value,
        selectNodes,
        selectSingle,
        evaluateNumber,
        evaluateBoolean,
        evaluateString,
        lastDefaultValue,
        getDsName,
        getNode,
        childNodes,
        localName,
        namespaceURI,
        setContentAsync,
        appendChildAsync,
        appendChildrenAsync,
        toString: localName,
        deleteSelf,
        insertBeforeAsync,
        insertAfterAsync,
        createElement,
        createElementNS,
        getAttribute,
    };

    return self;
}

export default dataSourceNode;
